import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  settingsService,
  type GeneralConfig,
  type GeneralUpdateInput,
} from '@/services/settings.service';
import { settingsKeys } from '@/services/queryKeys';

/** Setting hiếm khi đổi — cache 5 phút để POS/Products không refetch liên tục. */
const STALE_TIME_MS = 5 * 60_000;

export function useGeneralConfig() {
  return useQuery<GeneralConfig>({
    queryKey: settingsKeys.general(),
    queryFn: () => settingsService.getGeneral(),
    staleTime: STALE_TIME_MS,
    // Retry 2 lần: một blip mạng không được biến thành "setting đang tắt".
    retry: 2,
  });
}

export function useUpdateGeneralConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GeneralUpdateInput) => settingsService.updateGeneral(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.general() }),
  });
}

/**
 * Hook dùng chung cho Products form/list và POS.
 *
 * `enabled` fallback `false` khi đang load hoặc lỗi — CHỈ dùng cho HIỂN THỊ
 * (ẩn UI serial, không để nó loé lên rồi biến mất).
 *
 * `isResolved` phân biệt "đã biết chắc setting" với "chưa biết" (đang load hoặc
 * query lỗi). Mọi hành động có hậu quả (ghi dữ liệu, export file) PHẢI kiểm
 * `isResolved` trước — `enabled === false` khi chưa resolve KHÔNG có nghĩa là
 * tenant đã tắt serial.
 */
export function useSerialTrackingEnabled(): {
  enabled: boolean;
  isLoading: boolean;
  isResolved: boolean;
  isError: boolean;
} {
  const { data, isLoading, isSuccess, isError } = useGeneralConfig();
  return {
    enabled: data?.serial_tracking_enabled ?? false,
    isLoading,
    isResolved: isSuccess,
    isError,
  };
}
